import React, { useEffect, useState } from "react";
import { Box } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { ArrowBack } from "@mui/icons-material";

import { AddEditLead, LeadActivityCard, LeadDetails, LeadTasks } from "../../components/crm-pro";
import { LeadTaskOverview } from "../../components/crm-pro/LeadTaskOverview.component";
import { get_date } from "../../components/generalComponets";
import { PageHeader } from "../../components/v3";
import { CRM_PRO_DELETE_DOCUMENT, CRM_PRO_DELETE_LEAD, CRM_PRO_EDIT_LEAD, CRM_PRO_UPLOAD_DOCUMENT } from "../../constants";
import { PageLayout } from "../../layouts/PageLayout";
import { CRMService, LeadService } from "../../services";
import { useHasPermission } from "../../utils";
import { CRM_PRO_ROUTES } from "./constants";
import { RESTORE_TABLE_STATE } from "../../utils/tableStateStorage";

export const SingleLeadPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [leadDetails, setLeadDetails] = useState<any>({});
  const [selectedPriority, setSelectedPriority] = useState<any>(null);
  const [leadActivities, setLeadActivities] = useState<any>([]);
  const [leadTasks, setLeadTasks] = useState<any>([]);
  const [openEditModal, setOpenEditModal] = useState(false);

  const hasEditLead = useHasPermission(CRM_PRO_EDIT_LEAD);
  const hasDeleteLead = useHasPermission(CRM_PRO_DELETE_LEAD);
  const hasUploadDocument = useHasPermission(CRM_PRO_UPLOAD_DOCUMENT);
  const hasDeleteDocument = useHasPermission(CRM_PRO_DELETE_DOCUMENT);

  useEffect(() => {
    if (id) fetchData(id);
  }, [id]);

  const fetchData = async (leadId: string) => {
    try {
      const lead = await LeadService.getLeadById(Number(leadId));
      setLeadDetails(lead);
      setSelectedPriority(lead?.priority);

      const activities = await LeadService.getLeadActivities(Number(leadId));
      setLeadActivities(activities);

      const tasks = await LeadService.getLeadTasks(Number(leadId));
      setLeadTasks(tasks);
    } catch (error) {
      console.error("fetchData error:", error);
      // A view-assigned user loses access once their last open task on the lead is done or deleted.
      if (error?.response?.status === 403) {
        toast.info("This lead is no longer assigned to you");
        navigate(CRM_PRO_ROUTES.leads, { state: RESTORE_TABLE_STATE });
      }
    }
  };

  const leadTaskAssignees = leadTasks
    .filter((task) => task.assignee)
    .map((task) => task.assignee)
    .filter((assignee, index, self) => index === self.findIndex((a) => a.id === assignee.id));

  const handlePriorityChange = async (selected) => {
    try {
      await LeadService.trackLeadActivity(leadDetails.id, {
        priorityId: selected ? selected.id : undefined,
      });
      setSelectedPriority(selected);
      toast.success("Priority updated successfully");
      if (id) fetchData(id as string);
    } catch (error) {
      toast.error("Failed to update priority");
      console.error("handlePriorityChange error:", error);
    }
  };

  const handleEditLead = () => setOpenEditModal(true);
  const handleCloseEditModal = () => setOpenEditModal(false);
  const handleEditSuccess = () => {
    if (id) fetchData(id as string);
  };
  // "Back to list" restores the filters the leads list had, like the browser's Back button.
  const handleBackToList = () => navigate(CRM_PRO_ROUTES.leads, { state: RESTORE_TABLE_STATE });

  const sortedActivities = leadActivities
    ?.slice()
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

  return (
    <PageLayout>
      {/* header section */}
      <Box
        component="main"
        sx={{
          height: "calc(100vh - 75px - 21px)",
          position: "relative",
          minWidth: 0,
          width: "100%",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <PageHeader
          // title
          title="Lead Details"
          subtitle={get_date(leadDetails.createdAt)}
          // back button
          buttons={[
            {
              buttonText: "Back",
              onClick: handleBackToList,
              buttonIcon: ArrowBack,
            },
          ]}
          // select input
          inputFields={[
            {
              label: "Priority",
              value: selectedPriority,
              fetchData: () => CRMService.getLeadPriorities({ includeGlobal: true }),
              onChange: handlePriorityChange,
              disableClearable: true,
            },
          ]}
        />

        <Box sx={{ mt: -9, zIndex: 2, position: "relative", flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gridTemplateRows: "auto minmax(0, 1fr)",
              flex: 1,
              minHeight: 0,
              columnGap: 0,
              rowGap: 0,
            }}
          >
            {/* lead details and task overview */}
            <Box
              sx={{
                gridColumn: 1,
                gridRow: 1,
                minWidth: 0,
                mx: "22px",
                mt: "22px",
                mb: 0,
                "& > .MuiBox-root": { margin: 0 },
              }}
            >
              <LeadDetails
                leadDetails={leadDetails}
                leadTaskAssignees={leadTaskAssignees}
                onEdit={hasEditLead ? handleEditLead : undefined}
                onActivityChange={() => fetchData(id as string)}
                canUploadDocument={hasUploadDocument}
                canDeleteDocument={hasDeleteDocument}
              />
            </Box>

            <Box
              sx={{
                gridColumn: 2,
                gridRow: "1 / span 2",
                minHeight: 0,
                height: "100%",
                maxHeight: "100%",
                display: "flex",
                flexDirection: "column",
                minWidth: 0,
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  mx: "22px",
                  mt: "22px",
                  mb: 0,
                  "& > .MuiBox-root": { margin: 0 },
                }}
              >
                <LeadTaskOverview tasks={leadTasks} />
              </Box>

              <Box
                sx={{
                  mx: "22px",
                  mt: "22px",
                  mb: "22px",
                  flex: 1,
                  minHeight: 0,
                  "& > .MuiBox-root": { margin: 0 },
                }}
              >
                <LeadActivityCard title="Activities" activities={sortedActivities} />
              </Box>
            </Box>

            {/* tasks */}
            <Box
              sx={{
                gridColumn: 1,
                gridRow: 2,
                minHeight: 0,
                height: "100%",
                maxHeight: "100%",
                display: "flex",
                flexDirection: "column",
                minWidth: 0,
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  mx: "22px",
                  mt: 0,
                  mb: "22px",
                  flex: 1,
                  minHeight: 0,
                  "& > .MuiBox-root": { margin: 0 },
                }}
              >
                <LeadTasks tasks={leadTasks} onUpdate={() => fetchData(id as string)} />
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      <AddEditLead open={openEditModal} onClose={handleCloseEditModal} leadId={leadDetails?.id} onSuccess={handleEditSuccess} />
    </PageLayout>
  );
};
